<template>
  <div cy="confirm-modal-overlay" class="modal-overlay" @click.self="$emit('cancel')">
    <div cy="confirm-modal" class="modal" role="alertdialog" aria-modal="true">
      <div class="modal-head">
        <span class="modal-icon" aria-hidden="true">
          <img :src="icon || defaultIcon" alt="" width="18" height="18">
        </span>
        <p class="modal-title">{{ title || ('Șterge ' + type) }}</p>
      </div>

      <p v-if="message" class="modal-message">{{ message }}</p>
      <p v-else class="modal-message">
        Ești sigur că vrei să ștergi {{ type.toLowerCase() }} <strong>{{ clientName }}</strong>?
        Această acțiune nu poate fi anulată.
      </p>

      <div class="modal-actions flex">
        <button v-if="!hideCancel" cy="confirm-modal-cancel" class="btn-cancel" @click="$emit('cancel')">{{ cancelLabel || 'Anulează' }}</button>
        <button cy="confirm-modal-confirm" class="btn-delete" @click="$emit('confirm')">{{ confirmLabel || 'Șterge' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import trashIcon from '../assets/trash.svg'

export default {
  name: 'ConfirmDeleteModal',
  props: {
    // URL-ul icon-ului din header. Gol => coșul de gunoi (ștergere).
    icon: {
      type: String,
      default: ''
    },
    clientName: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'Client'
    },
    // Opționale: când sunt date, suprascriu textul implicit de ștergere, ca să
    // putem refolosi același modal și pentru alte confirmări (ex. reset deviz).
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    confirmLabel: {
      type: String,
      default: ''
    },
    cancelLabel: {
      type: String,
      default: ''
    },
    // Ascunde butonul de anulare => modal informativ cu un singur buton.
    hideCancel: {
      type: Boolean,
      default: false
    }
  },
  emits: ['confirm', 'cancel'],
  computed: {
    defaultIcon () {
      return trashIcon
    }
  }
}
</script>

<style lang="less">
@media only screen and (min-width: 0) {
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.5);
    display: flex;
    // Sit the dialog a bit above the vertical centre — roughly eye level.
    align-items: flex-start;
    justify-content: center;
    z-index: 1000;
    animation: modal-overlay-in 140ms ease-out;
  }

  .modal {
    background: #FFFFFF;
    // 4px radius is the house style — kept on the card, the icon badge and the
    // buttons so the whole dialog reads consistent.
    border-radius: 4px;
    padding: 22px 24px;
    width: 420px;
    max-width: calc(100vw - 32px);
    margin-top: 18vh;
    box-shadow: 0 12px 32px rgba(17, 24, 39, 0.16);
    animation: modal-pop 170ms cubic-bezier(0.2, 0.8, 0.3, 1) both;

    // Left-aligned header: small icon chip beside the title — calmer than a big
    // centred badge, and keeps red use minimal.
    .modal-head {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;

      .modal-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: 34px;
        height: 34px;
        border-radius: 4px;
        background: #FEECEC;
      }

      .modal-title {
        font-size: 19px;
        font-weight: 600;
        color: #111827;
        margin: 0;
      }
    }

    .modal-message {
      font-size: 16px;
      line-height: 1.55;
      color: #6B7280;
      margin: 0 0 22px;

      strong {
        color: #111827;
        font-weight: 600;
      }
    }

    // Right-aligned footer (desktop convention): neutral exit + destructive
    // action, auto width so neither dominates.
    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;

      button {
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        border: 1px solid transparent;
        transition: background-color 0.15s ease, border-color 0.15s ease;
      }

      .btn-cancel {
        background-color: #FFFFFF;
        color: #374151;
        border-color: #E5E7EB;

        &:hover {
          background-color: #F9FAFB;
          border-color: #D1D5DB;
        }
      }

      // Reserved, non-neon red — stands out as the destructive action without
      // flooding the dialog in colour.
      .btn-delete {
        background-color: #DC2626;
        color: #FFFFFF;

        &:hover {
          background-color: #B91C1C;
        }
      }
    }
  }

  // Variantă informativă (ex. „deviz gol"): neutră (gri, nu roșu) și centrată
  // vertical. Nu atinge varianta implicită de ștergere/reset (roșie, sus).
  .modal-overlay.info-notice {
    align-items: center;

    .modal {
      // Centrat vertical, apoi ridicat puțin peste mijloc.
      margin-top: 0;
      margin-bottom: 30vh;
    }

    .modal-head {
      justify-content: center;
    }

    // Fără icon în notice-ul informativ — doar titlul, centrat.
    .modal-head .modal-icon {
      display: none;
    }

    .modal-actions .btn-delete {
      width: 100%;
      text-align: center;
      background-color: #a35555;

      &:hover {
        background-color: #8c4848;
      }
    }
  }

  @keyframes modal-overlay-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  @keyframes modal-pop {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  @media (prefers-reduced-motion: reduce) {
    .modal-overlay,
    .modal {
      animation: none;
    }
  }
}

/* On desktop the left menu is a fixed sidebar, so offset the overlay by its
   width — the dialog then centres within the content area, not the whole
   viewport (which would read as shifted left). */
@media only screen and (min-width: 1024px) {
  .modal-overlay {
    left: var(--sidebar-width);
  }
}
</style>
